import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  computed,
  EventEmitter,
  inject,
  input,
  OnDestroy,
  Output,
  QueryList,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';

import { map, merge, of, startWith, Subscription, switchMap } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { SelectableCardFooterComponent } from './selectable-card-footer.component';

interface SelectableCardControlState {
  disabled: boolean;
  selected: boolean;
}

@Component({
  selector: 'fcom-selectable-card',
  templateUrl: './selectable-card.component.html',
  styleUrls: ['./selectable-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectableCardComponent implements AfterContentInit, OnDestroy {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private footerChangesSubscription?: Subscription;

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly checked = input(false);
  readonly describedBy = input<string>();
  readonly control = input<AbstractControl | null>(null);
  readonly disabled = input(false);
  readonly inputId = input<string>();
  readonly interactive = input(true);
  readonly testId = input<string>();

  private readonly controlState = toSignal(
    toObservable(this.control).pipe(
      switchMap((control) => {
        if (!control) {
          return of(undefined);
        }

        return merge(control.valueChanges, control.statusChanges).pipe(
          startWith(null),
          map((): SelectableCardControlState => ({
            disabled: control.disabled,
            selected: !!control.value,
          }))
        );
      })
    ),
    { initialValue: undefined }
  );

  readonly isSelected = computed(() => this.controlState()?.selected ?? this.checked());
  readonly isReadonly = computed(() => !this.interactive() || !this.inputId());
  readonly isSelectable = computed(() => !this.isReadonly());
  readonly isDisabled = computed(() => !this.isReadonly() && (this.controlState()?.disabled ?? this.disabled()));
  readonly isInteractive = computed(() => this.isSelectable() && !this.isDisabled());

  @ContentChildren(SelectableCardFooterComponent)
  footerComponents?: QueryList<SelectableCardFooterComponent>;

  @Output()
  selectionChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  get hasFooter(): boolean {
    return !!this.footerComponents?.length;
  }

  ngAfterContentInit(): void {
    this.footerChangesSubscription = this.footerComponents?.changes.subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.footerChangesSubscription?.unsubscribe();
  }

  onSelectionChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;

    if (target && this.isInteractive() && !this.control()) {
      this.selectionChange.emit(target.checked);
    }
  }
}
